import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Helmet } from "react-helmet-async";
import "./HelpCenter.scss";
import DynamicHeader from "../../Header/DynamicHeader";
import hotLineIcon from "../img/hot-line.png";
import liveChatIcon from "../img/live-chat.png";
import feedbackIcon from "../img/feedback.png";

const getDeviceInfo = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      ua: navigator.userAgent,
      lang: navigator.language,
      platform: navigator.platform,
      screen: `${window.screen.width}x${window.screen.height}`,
      tz,
      time: new Date().toISOString(),
      online: navigator.onLine ? "online" : "offline",
    };
  } catch {
    return {};
  }
};

const PHONE_PREFIX = "+380";

const MONTH_NAMES = [
  "Січень",
  "Лютий",
  "Березень",
  "Квітень",
  "Травень",
  "Червень",
  "Липень",
  "Серпень",
  "Вересень",
  "Жовтень",
  "Листопад",
  "Грудень",
];

const WEEKDAYS_SHORT = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "НД"];

const pad2 = (n) => String(n).padStart(2, "0");
const formatDateInput = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const formatTimeInput = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

// константа висоти рядка в колесі
const WHEEL_ITEM_HEIGHT = 36;

// масиви годин і хвилин (24-годинний формат)
const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad2(i));

const buildMonthMatrix = (year, month) => {
  // month: 0-11
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // перший день тижня – понеділок
  const jsDay = first.getDay(); // 0 неділя … 6 субота
  const startOffset = (jsDay + 6) % 7; // 0 = понеділок

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const HelpCenter = () => {
  const API_BASE =
    process.env.REACT_APP_API_BASE ||
    process.env.REACT_APP_API ||
    (typeof window !== "undefined" &&
    window.location.hostname.includes("onrender.com")
      ? "https://cvanto-api.onrender.com"
      : "http://localhost:5000");

  const RAW_HELP_ENDPOINT = process.env.REACT_APP_HELP_CALLBACK_ENDPOINT;
  const HELP_CALLBACK_ENDPOINT =
    RAW_HELP_ENDPOINT && /^https?:\/\//.test(RAW_HELP_ENDPOINT)
      ? RAW_HELP_ENDPOINT
      : `${API_BASE}/api/help/callback`;

  const SUPPORT_PHONE =
    process.env.REACT_APP_SUPPORT_PHONE || "+380673011173";

  const SUPPORT_TELEGRAM_USERNAME =
  process.env.REACT_APP_SUPPORT_TELEGRAM_USERNAME || "cvanto_support";

  const SUPPORT_EMAIL =
    process.env.REACT_APP_SUPPORT_EMAIL || "support@cvanto.com";

  const [open, setOpen] = useState({
    hotline: false,
    chat: false,
    callback: false,
  });

  const phoneRef = useRef(null);
  const timeInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);

  const isMobileDevice = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isTouch =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0;
    const isSmall = window.matchMedia
      ? window.matchMedia("(max-width: 960px)").matches
      : window.innerWidth <= 960;
    const isMobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    return Boolean(isTouch && (isSmall || isMobileUA));
  }, []);

  const initialSchedule = useMemo(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 15);
    now.setSeconds(0, 0);
    return {
      dateTime: now,
      year: now.getFullYear(),
      month: now.getMonth(),
    };
  }, []);

  const [schedule, setSchedule] = useState(initialSchedule);

  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [tempHour, setTempHour] = useState(
    initialSchedule.dateTime.getHours()
  );
  const [tempMinute, setTempMinute] = useState(
    initialSchedule.dateTime.getMinutes()
  );

  const [cbForm, setCbForm] = useState({
    name: "",
    phone: PHONE_PREFIX,
    topic: "",
    callOption: "asap",
    date: "",
    time: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const device = useMemo(() => getDeviceInfo(), []);

  const syncScheduleToForm = useCallback((dt) => {
    if (!dt || Number.isNaN(dt.getTime())) return;
    const date = formatDateInput(dt);
    const time = formatTimeInput(dt);
    setCbForm((prev) => ({
      ...prev,
      date,
      time,
    }));
  }, []);

  const applyTime = useCallback(
    (hour, minute) => {
      const safeHour = Math.min(Math.max(hour, 0), 23);
      const safeMinute = Math.min(Math.max(minute, 0), 59);

      setSchedule((prev) => {
        const dt = new Date(prev.dateTime);
        dt.setHours(safeHour);
        dt.setMinutes(safeMinute);
        dt.setSeconds(0, 0);
        return {
          ...prev,
          dateTime: dt,
        };
      });

      const val = `${pad2(safeHour)}:${pad2(safeMinute)}`;
      setCbForm((prev) => ({
        ...prev,
        time: val,
      }));
    },
    [setSchedule, setCbForm]
  );

  const applyDate = useCallback(
    (yyyyMmDd) => {
      if (!yyyyMmDd) return;
      const [y, m, d] = String(yyyyMmDd).split("-").map((x) => parseInt(x, 10));
      if (!y || !m || !d) return;

      setSchedule((prev) => {
        const dt = new Date(prev.dateTime);
        dt.setFullYear(y, m - 1, d);
        dt.setSeconds(0, 0);
        return {
          ...prev,
          year: dt.getFullYear(),
          month: dt.getMonth(),
          dateTime: dt,
        };
      });

      setCbForm((prev) => ({
        ...prev,
        date: yyyyMmDd,
      }));
    },
    [setSchedule, setCbForm]
  );

  useEffect(() => {
    syncScheduleToForm(schedule.dateTime);
  }, [schedule.dateTime, syncScheduleToForm]);

  useEffect(() => {
    if (cbForm.callOption === "schedule") {
      syncScheduleToForm(schedule.dateTime);
    }
  }, [cbForm.callOption, schedule.dateTime, syncScheduleToForm]);

  useEffect(() => {
    if (!timePickerOpen) return;

    const hRef = hourListRef.current;
    const mRef = minuteListRef.current;
    const hTop = tempHour * WHEEL_ITEM_HEIGHT;
    const mTop = tempMinute * WHEEL_ITEM_HEIGHT;

    if (hRef) {
      if (typeof hRef.scrollTo === "function") {
        hRef.scrollTo(0, hTop);
      } else {
        hRef.scrollTop = hTop;
      }
    }
    if (mRef) {
      if (typeof mRef.scrollTo === "function") {
        mRef.scrollTo(0, mTop);
      } else {
        mRef.scrollTop = mTop;
      }
    }
  }, [timePickerOpen, tempHour, tempMinute]);

  useEffect(() => {
    if (!timePickerOpen) return;

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [timePickerOpen]);

  const monthCells = useMemo(
    () => buildMonthMatrix(schedule.year, schedule.month),
    [schedule.year, schedule.month]
  );

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const mailtoWithDetails = useCallback(
    (subjectPrefix = "Запит до підтримки", extra = "") => {
      const subject = `${subjectPrefix} | Cvanto`;
      const body = [
        extra || "Опишіть проблему тут (коротко й по суті):",
        "",
        "--- Технічні деталі ---",
        `Браузер: ${device.ua || "-"}`,
        `Мова: ${device.lang || "-"}`,
        `Платформа: ${device.platform || "-"}`,
        `Екран: ${device.screen || "-"}`,
        `Статус мережі: ${device.online || "-"}`,
        `Час/часовий пояс: ${device.time || "-"} / ${device.tz || "-"}`,
      ].join("\n");

      return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
    },
    [SUPPORT_EMAIL, device]
  );

  const openChat = () => {
    const telegramUrl = `https://t.me/${SUPPORT_TELEGRAM_USERNAME}`;
    window.open(telegramUrl, "_blank", "noopener,noreferrer");
  };

  const clampCaret = (inputEl) => {
    if (!inputEl) return;
    const min = PHONE_PREFIX.length;
    const start = inputEl.selectionStart ?? 0;
    const end = inputEl.selectionEnd ?? 0;

    if (start < min || end < min) {
      inputEl.setSelectionRange(min, min);
    }
  };

  const normalizePhone = (val) => {
    if (!val.startsWith(PHONE_PREFIX)) {
      val = val.replace(/^\+?3?8?0?/, "");
      val = PHONE_PREFIX + val;
    }
    const rest = val.slice(PHONE_PREFIX.length).replace(/[^\d\s()-]/g, "");
    return PHONE_PREFIX + rest;
  };

  const handlePhoneChange = (e) => {
    const el = e.target;
    const next = normalizePhone(el.value);
    setCbForm((prev) => ({ ...prev, phone: next }));
    requestAnimationFrame(() => clampCaret(phoneRef.current));
  };

  const handlePhoneKeyDown = (e) => {
    const el = e.currentTarget;
    const min = PHONE_PREFIX.length;

    if (
      (e.key === "Backspace" && (el.selectionStart ?? 0) <= min) ||
      (e.key === "Delete" && (el.selectionStart ?? 0) < min)
    ) {
      e.preventDefault();
      return;
    }

    if (e.key === "Home") {
      e.preventDefault();
      el.setSelectionRange(min, min);
    }

    if (e.key === "ArrowLeft" && (el.selectionStart ?? 0) <= min) {
      e.preventDefault();
      el.setSelectionRange(min, min);
    }
  };

  const handlePhoneFocusOrClick = () => {
    requestAnimationFrame(() => clampCaret(phoneRef.current));
  };

  const handleMonthChange = (delta) => {
    setSchedule((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return {
        ...prev,
        year: d.getFullYear(),
        month: d.getMonth(),
      };
    });
  };

  const handlePickDay = (day) => {
    if (!day) return;
    setSchedule((prev) => {
      const dt = new Date(prev.dateTime);
      dt.setFullYear(prev.year, prev.month, day);
      dt.setSeconds(0, 0);
      return {
        ...prev,
        dateTime: dt,
      };
    });
  };

  const handleTimeChange = (e) => {
    const val = e.target.value; // HH:MM
    if (!val) return;
    const [hStr, mStr] = val.split(":");
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    applyTime(h, m);
  };

  const handleDateChange = (e) => {
    const val = e.target.value; // YYYY-MM-DD
    if (!val) return;
    applyDate(val);
  };

  const openNativeTimePicker = () => {
    const dt = schedule.dateTime;
    setTempHour(dt.getHours());
    setTempMinute(dt.getMinutes());

    if (isMobileDevice && timeInputRef.current) {
      const el = timeInputRef.current;
      try {
        if (typeof el.showPicker === "function") {
          el.showPicker();
          return;
        }
      } catch {}
      el.focus();
      el.click();
      return;
    }

    setTimePickerOpen(true);
  };

  const openNativeDatePicker = () => {
    if (isMobileDevice && dateInputRef.current) {
      const el = dateInputRef.current;
      try {
        if (typeof el.showPicker === "function") {
          el.showPicker();
          return;
        }
      } catch {}
      el.focus();
      el.click();
      return;
    }
  };

  const submitCallback = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSent(false);

    const name = cbForm.name.trim();
    const phone = cbForm.phone.trim();
    const topic = cbForm.topic.trim();

    const phoneOk =
      phone.startsWith(PHONE_PREFIX) &&
      /\d{7,}/.test(phone.slice(PHONE_PREFIX.length).replace(/\D/g, ""));

    if (!name || !phoneOk) {
      setError("Перевірте ім’я та телефон (+380...)");
      setSubmitting(false);
      return;
    }

    const scheduleObj =
      cbForm.callOption === "schedule" && cbForm.date && cbForm.time
        ? { date: cbForm.date, time: cbForm.time }
        : null;

    try {
      const payload = {
        name,
        phone,
        note: topic,
        schedule: scheduleObj,
        device,
      };

      const res = await fetch(HELP_CALLBACK_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "Не вдалося надіслати заявку");
      }

      setSent(true);
      setCbForm({
        name: "",
        phone: PHONE_PREFIX,
        topic: "",
        callOption: "asap",
        date: "",
        time: "",
      });
      setSchedule(initialSchedule);
    } catch (err) {
      setError(err.message || "Сталася помилка. Спробуйте ще раз.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (key) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedDate = schedule.dateTime;
  const isToday = (year, month, day) => {
    if (!day) return false;
    return (
      today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === day
    );
  };

  const isSelected = (year, month, day) => {
    if (!day || !selectedDate) return false;
    return (
      selectedDate.getFullYear() === year &&
      selectedDate.getMonth() === month &&
      selectedDate.getDate() === day
    );
  };

  const formattedTimeLabel = selectedDate
    ? selectedDate.toLocaleTimeString("uk-UA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "--:--";

  const formattedDateLabel = selectedDate
    ? selectedDate.toLocaleDateString("uk-UA", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "—";

  const handleHourScroll = (e) => {
    const idx = Math.round(e.currentTarget.scrollTop / WHEEL_ITEM_HEIGHT);
    const clamped = Math.min(Math.max(idx, 0), 23);
    setTempHour(clamped);
  };

  const handleMinuteScroll = (e) => {
    const idx = Math.round(e.currentTarget.scrollTop / WHEEL_ITEM_HEIGHT);
    const clamped = Math.min(Math.max(idx, 0), 59);
    setTempMinute(clamped);
  };

  const handleHourClick = (idx) => {
    const hRef = hourListRef.current;
    if (hRef) {
      hRef.scrollTop = idx * WHEEL_ITEM_HEIGHT;
    }
    setTempHour(idx);
  };

  const handleMinuteClick = (idx) => {
    const mRef = minuteListRef.current;
    if (mRef) {
      mRef.scrollTop = idx * WHEEL_ITEM_HEIGHT;
    }
    setTempMinute(idx);
  };

  const handleConfirmTime = () => {
    applyTime(tempHour, tempMinute);
    setTimePickerOpen(false);
  };

  const handleCloseTimePicker = () => {
    setTimePickerOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Допомога та техпідтримка — Cvanto</title>
        <meta name="description" content="Відповіді на часті запитання та контакти техпідтримки Cvanto." />
        <link rel="canonical" href="https://cvanto.com/settings/help" />
      </Helmet>

      <DynamicHeader
        title="Допомога та техпідтримка"
        variant="glass"
        size="regular"
        showBack
        withSpacer
      />

      <div
        className="support-center"
        role="region"
        aria-label="Help Center"
      >
        <header className="sc-hero">
          <h1 className="sc-title">Допомога та техпідтримка</h1>
          <p className="sc-subtitle">
            Чат із консультантом, гаряча лінія або зворотний дзвінок.
          </p>
        </header>

        <section className="sc-tiles">
          <div className={`tile-group ${open.hotline ? "open" : ""}`}>
            <button
              className="tile__head tile__head--ghost"
              onClick={() => toggle("hotline")}
              aria-expanded={open.hotline}
            >
              <img src={hotLineIcon} alt="" className="tile__icon" />
              <div className="tile__title">Гаряча лінія</div>
              <div className="tile__chev" aria-hidden>
                ▾
              </div>
            </button>

            <div
              className="tile__panel"
              role="region"
              aria-label="Hotline panel"
            >
              <div className="tile__panel-inner">
                <p className="tile__lead">
                  На зв’язку спеціаліст Cvanto. Можна швидко вирішити питання
                  телефоном.
                </p>
                <a
                  className="btn btn--primary"
                  href={`tel:${SUPPORT_PHONE}`}
                >
                  Зателефонувати на гарячу лінію
                </a>
              </div>
            </div>
          </div>

          <div className={`tile-group ${open.chat ? "open" : ""}`}>
            <button
              className="tile__head tile__head--ghost"
              onClick={() => toggle("chat")}
              aria-expanded={open.chat}
            >
              <img src={liveChatIcon} alt="" className="tile__icon" />
              <div className="tile__title">
                Написати в Telegram консультанту
              </div>
              <div className="tile__chev" aria-hidden>
                ▾
              </div>
            </button>

            <div
              className="tile__panel"
              role="region"
              aria-label="Telegram chat panel"
            >
              <div className="tile__panel-inner">
                <p className="tile__lead">
                  У Telegram відповідає спеціаліст Cvanto — жива підтримка,
                  швидко і по суті.
                </p>
                <div className="sc-cta">
                  <button
                    className="btn btn--primary"
                    onClick={openChat}
                  >
                    Відкрити чат
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={`tile-group ${open.callback ? "open" : ""}`}>
            <button
              className="tile__head tile__head--ghost"
              onClick={() => toggle("callback")}
              aria-expanded={open.callback}
            >
              <img src={feedbackIcon} alt="" className="tile__icon" />
              <div className="tile__title">Зворотний дзвінок</div>
              <div className="tile__chev" aria-hidden>
                ▾
              </div>
            </button>

            <div
              className="tile__panel"
              role="region"
              aria-label="Callback panel"
            >
              <div className="tile__panel-inner">
                <form className="cb-form" onSubmit={submitCallback}>
                  <input
                    placeholder="Ім’я"
                    value={cbForm.name}
                    onChange={(e) =>
                      setCbForm({ ...cbForm, name: e.target.value })
                    }
                    required
                  />
                  <input
                    ref={phoneRef}
                    placeholder="+380"
                    value={cbForm.phone}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyDown}
                    onFocus={handlePhoneFocusOrClick}
                    onClick={handlePhoneFocusOrClick}
                    inputMode="tel"
                    aria-label="Телефон"
                    required
                  />
                  <input
                    placeholder="Коротко про питання (опційно)"
                    value={cbForm.topic}
                    onChange={(e) =>
                      setCbForm({ ...cbForm, topic: e.target.value })
                    }
                  />

                  <div
                    className="cc-radio-group"
                    role="radiogroup"
                    aria-label="Варіант зворотного дзвінка"
                  >
                    <label className="cc-radio">
                      <input
                        type="radio"
                        name="callOption"
                        value="asap"
                        checked={cbForm.callOption === "asap"}
                        onChange={() =>
                          setCbForm((prev) => ({
                            ...prev,
                            callOption: "asap",
                          }))
                        }
                      />
                      <span
                        className="cc-radio-mark"
                        aria-hidden="true"
                      />
                      <span className="cc-radio-text">
                        Передзвоніть якнайшвидше
                      </span>
                    </label>

                    <label className="cc-radio">
                      <input
                        type="radio"
                        name="callOption"
                        value="schedule"
                        checked={cbForm.callOption === "schedule"}
                        onChange={() =>
                          setCbForm((prev) => ({
                            ...prev,
                            callOption: "schedule",
                          }))
                        }
                      />
                      <span
                        className="cc-radio-mark"
                        aria-hidden="true"
                      />
                      <span className="cc-radio-text">
                        Передзвоніть у конкретний час
                      </span>
                    </label>
                  </div>

                  {cbForm.callOption === "schedule" && (
                    <div className="cc-schedule-block">
                      {isMobileDevice ? (
                        <>
                          <div className="cc-time-row cc-date-row">
                            <div className="cc-time-label">Дата</div>

                            <div className="cc-native-field">
                              <button
                                type="button"
                                className="cc-time-pill"
                                onClick={openNativeDatePicker}
                              >
                                {formattedDateLabel}
                              </button>

                              <input
                                ref={dateInputRef}
                                className="cc-native-input-overlay"
                                type="date"
                                value={
                                  cbForm.date || formatDateInput(selectedDate)
                                }
                                onChange={handleDateChange}
                              />
                            </div>
                          </div>

                          <div className="cc-time-row">
                            <div className="cc-time-label">Час</div>

                            <div className="cc-native-field">
                              <button
                                type="button"
                                className="cc-time-pill"
                                onClick={openNativeTimePicker}
                              >
                                {formattedTimeLabel}
                              </button>

                              <input
                                ref={timeInputRef}
                                className="cc-native-input-overlay"
                                type="time"
                                value={
                                  cbForm.time || formatTimeInput(selectedDate)
                                }
                                onChange={handleTimeChange}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="cc-calendar">
                            <div className="cc-cal-header">
                              <button
                                type="button"
                                className="cc-cal-nav"
                                onClick={() => handleMonthChange(-1)}
                                aria-label="Попередній місяць"
                              >
                                ‹
                              </button>
                              <div className="cc-cal-title">
                                {MONTH_NAMES[schedule.month]}{" "}
                                {schedule.year}
                              </div>
                              <button
                                type="button"
                                className="cc-cal-nav"
                                onClick={() => handleMonthChange(1)}
                                aria-label="Наступний місяць"
                              >
                                ›
                              </button>
                            </div>

                            <div className="cc-cal-grid">
                              {WEEKDAYS_SHORT.map((d) => (
                                <div
                                  key={d}
                                  className="cc-cal-weekday"
                                >
                                  {d}
                                </div>
                              ))}
                              {monthCells.map((day, idx) => {
                                if (!day) {
                                  return (
                                    <div
                                      key={`e-${idx}`}
                                      className="cc-cal-empty"
                                    />
                                  );
                                }
                                const selected = isSelected(
                                  schedule.year,
                                  schedule.month,
                                  day
                                );
                                const todayFlag = isToday(
                                  schedule.year,
                                  schedule.month,
                                  day
                                );
                                const cls = [
                                  "cc-cal-day",
                                  selected ? "is-selected" : "",
                                  todayFlag ? "is-today" : "",
                                ]
                                  .join(" ")
                                  .trim();
                                return (
                                  <button
                                    type="button"
                                    key={day + "-" + idx}
                                    className={cls}
                                    onClick={() => handlePickDay(day)}
                                  >
                                    {day}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="cc-time-row">
                            <div className="cc-time-label">Час</div>
                            <button
                              type="button"
                              className="cc-time-pill"
                              onClick={openNativeTimePicker}
                            >
                              {formattedTimeLabel}
                            </button>

                            <input
                              ref={timeInputRef}
                              className="cc-time-native"
                              type="time"
                              value={
                                cbForm.time ||
                                formatTimeInput(selectedDate)
                              }
                              onChange={handleTimeChange}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <button
                    className="btn btn--primary cb-submit"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Надсилаємо…"
                      : cbForm.callOption === "schedule" &&
                        cbForm.date &&
                        cbForm.time
                      ? `Надіслати заявку на ${cbForm.date} о ${cbForm.time}`
                      : "Надіслати заявку"}
                  </button>

                  {sent && (
                    <span className="ok">Заявку надіслано. Дякуємо!</span>
                  )}
                  {error && <span className="err">{error}</span>}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="sc-card">
          <h3>Поширені питання</h3>
          <div className="faq">
            <details>
              <summary>Чи безкоштовні оголошення?</summary>
              <div className="faq-body">
                <p>
                  <b>Так</b> Публікація оголошень у Cvanto повністю безкоштовна.
                </p>
                <p>
                  <b>
                    Ми на етапі розвитку платформи, тому користувачі можуть
                    створювати оголошення без жодних оплат.
                  </b>
                </p>
              </div>
            </details>

            <details>
              <summary>Що таке спільноти у Cvanto?</summary>
              <div className="faq-body">
                <p>
                  Це тематичні простори, де можна ділитися постами та
                  спілкуватися з людьми за інтересами.
                </p>
              </div>
            </details>

            <details>
              <summary>Як працює безпека?</summary>
              <div className="faq-body">
                <p>
                  Ви самі вирішуєте, які контакти показувати. Є можливість
                  блокування користувачів. Можна поскаржитися на оголошення або
                  профіль.
                </p>
              </div>
            </details>

            <details>
              <summary>Що таке вітрина у Cvanto?</summary>
              <div className="faq-body">
                <p>
                  Це персональний простір майстра або спеціаліста, де видно
                  напрям роботи, підхід, досвід і формат роботи.
                </p>
              </div>
            </details>

            <details>
              <summary>Чи є мобільний додаток?</summary>
              <div className="faq-body">
                <p>
                  Поки що доступна веб-версія, яка адаптована для мобільних
                  пристроїв. Додаток планується.
                </p>
              </div>
            </details>
          </div>
        </section>
      </div>

      {timePickerOpen && (
        <div
          className="cc-time-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Вибір часу для зворотного дзвінка"
          onClick={handleCloseTimePicker}
        >
          <div
            className="cc-time-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cc-time-dialog-header">
              <button
                type="button"
                className="cc-time-dialog-close"
                onClick={handleCloseTimePicker}
                aria-label="Закрити"
              >
                ✕
              </button>
              <span className="cc-time-dialog-title">
                Оберіть час
              </span>
            </div>

            <div className="cc-time-wheel">
              <div className="cc-wheel-col">
                <div
                  ref={hourListRef}
                  className="cc-wheel-list"
                  onScroll={handleHourScroll}
                >
                  {HOURS.map((h, idx) => (
                    <button
                      key={h}
                      type="button"
                      className={
                        "cc-wheel-item" +
                        (idx === tempHour ? " is-active" : "")
                      }
                      onClick={() => handleHourClick(idx)}
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <div className="cc-wheel-window" aria-hidden="true" />
              </div>

              <div className="cc-wheel-separator">:</div>

              <div className="cc-wheel-col">
                <div
                  ref={minuteListRef}
                  className="cc-wheel-list"
                  onScroll={handleMinuteScroll}
                >
                  {MINUTES.map((m, idx) => (
                    <button
                      key={m}
                      type="button"
                      className={
                        "cc-wheel-item" +
                        (idx === tempMinute ? " is-active" : "")
                      }
                      onClick={() => handleMinuteClick(idx)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <div className="cc-wheel-window" aria-hidden="true" />
              </div>
            </div>

            <div className="cc-time-dialog-footer">
              <button
                type="button"
                className="btn cc-time-btn-cancel"
                onClick={handleCloseTimePicker}
              >
                Скасувати
              </button>
              <button
                type="button"
                className="btn btn--primary cc-time-btn-ok"
                onClick={handleConfirmTime}
              >
                Готово
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpCenter;